import { z } from 'zod'
import { createPaymentRequestInputSchema } from './accounting.ts'
import {
  decimalSchema,
  moneySchema,
  positiveDecimalSchema,
  timestampSchema,
  uuidSchema,
} from './common.ts'
import { warehouseUnitCodeSchema } from './units.ts'

/** Why a material is on «Що купити». Quantities are in the material's base unit. */
export const purchaseNeedReasonSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('shortage'),
    orderId: uuidSchema,
    orderNumber: z.string(),
    /** Still needed by this order beyond what is reserved for it. */
    quantity: decimalSchema,
  }),
  z.object({ kind: z.literal('critical'), threshold: decimalSchema }),
  z.object({
    kind: z.literal('request'),
    requestId: uuidSchema,
    requestedByName: z.string(),
    reason: z.string(),
    quantity: decimalSchema,
  }),
])
export type PurchaseNeedReason = z.infer<typeof purchaseNeedReasonSchema>

/** One material to buy, with every reason, what is already on its way, and a suggestion. */
export const purchaseNeedSchema = z.object({
  materialId: uuidSchema,
  materialName: z.string(),
  materialGrade: z.string().nullable(),
  baseUnit: z.string(),
  baseUnitSymbol: z.string(),
  available: decimalSchema,
  criticalThreshold: decimalSchema.nullable(),
  incoming: decimalSchema,
  suggested: decimalSchema,
  reasons: z.array(purchaseNeedReasonSchema),
})
export type PurchaseNeed = z.infer<typeof purchaseNeedSchema>

/** A request to buy without a production stage: sales, engineering, production, the warehouse. */
export const createPurchaseRequestInputSchema = z.strictObject({
  materialId: uuidSchema,
  quantity: positiveDecimalSchema,
  unit: warehouseUnitCodeSchema,
  reason: z.string().trim().min(1).max(500),
})
export type CreatePurchaseRequestInput = z.infer<typeof createPurchaseRequestInputSchema>

export const purchaseRequestSchema = z.object({
  id: uuidSchema,
  materialId: uuidSchema,
  /** Converted to the material's base unit when the request was made. */
  quantity: decimalSchema,
  unit: z.string(),
  reason: z.string(),
  state: z.enum(['open', 'completed', 'cancelled']),
  requestedBy: z.string(),
  requestedByName: z.string(),
  createdAt: timestampSchema,
})
export type PurchaseRequest = z.infer<typeof purchaseRequestSchema>

/** One material in a supplier order. `unitCost` is UAH per base unit, as agreed with the supplier. */
export const supplierOrderLineInputSchema = z.strictObject({
  materialId: uuidSchema,
  quantity: positiveDecimalSchema,
  unit: warehouseUnitCodeSchema,
  unitCost: moneySchema,
  /** Open standalone requests this line covers; they close when the order is placed. */
  requestIds: z.array(uuidSchema).max(50).default([]),
})

/** The storekeeper orders from one supplier for one paying company (spec §12.6.2). */
export const placeSupplierOrderInputSchema = z.strictObject({
  supplierId: uuidSchema,
  payerEntityId: uuidSchema,
  locationId: uuidSchema,
  expectedAt: z.iso.date().nullable().default(null),
  supplierInvoiceNumber: z.string().trim().min(1).max(100).nullable().default(null),
  lines: z
    .array(supplierOrderLineInputSchema)
    .min(1)
    .max(50)
    .refine(
      (lines) => new Set(lines.map((line) => line.materialId)).size === lines.length,
      'Кожен матеріал можна додати лише один раз',
    ),
})
export type PlaceSupplierOrderInput = z.infer<typeof placeSupplierOrderInputSchema>

/**
 * «Змінити» on «Замовлено»: the date and the invoice for the whole order, or cancel what has not
 * arrived. Lines already received keep their facts.
 */
export const updateSupplierOrderInputSchema = z
  .strictObject({
    expectedAt: z.iso.date().nullable().optional(),
    supplierInvoiceNumber: z.string().trim().min(1).max(100).nullable().optional(),
    cancel: z.literal(true).optional(),
  })
  .refine((input) => Object.keys(input).length > 0, 'Нічого не змінено')
export type UpdateSupplierOrderInput = z.infer<typeof updateSupplierOrderInputSchema>

export const SUPPLIER_ORDER_DELIVERY = [
  'ordered',
  'in_transit',
  'partially_received',
  'received',
  'cancelled',
] as const

/** «Замовлено»: one supplier order with its lines; delivery is derived from the lines. */
export const supplierOrderSchema = z.object({
  orderNumber: z.string(),
  supplierId: uuidSchema,
  supplierName: z.string(),
  payerEntityId: uuidSchema.nullable(),
  payerEntityName: z.string().nullable(),
  supplierInvoiceNumber: z.string().nullable(),
  expectedAt: z.iso.date().nullable(),
  createdAt: timestampSchema,
  delivery: z.enum(SUPPLIER_ORDER_DELIVERY),
  /** Null while any line has no agreed unit cost (supplies placed before costs were kept). */
  amount: decimalSchema.nullable(),
  lines: z.array(
    z.object({
      supplyId: uuidSchema,
      materialId: uuidSchema,
      materialName: z.string(),
      baseUnitSymbol: z.string(),
      quantity: decimalSchema,
      received: decimalSchema,
      unitCost: decimalSchema.nullable(),
      status: z.enum(SUPPLIER_ORDER_DELIVERY).or(z.literal('draft')),
    }),
  ),
  payment: z
    .object({ id: uuidSchema, status: z.enum(['pending', 'approved', 'rejected', 'paid']) })
    .nullable(),
})
export type SupplierOrder = z.infer<typeof supplierOrderSchema>

/**
 * One payment request for a whole supplier order (prototype `requestSupplyPayment`). Supplier,
 * paying company, purpose and amount come from the order; only the invoice facts are entered.
 */
export const requestSupplierOrderPaymentInputSchema = createPaymentRequestInputSchema
  .pick({
    supplierInvoiceNumber: true,
    supplierInvoiceDate: true,
    recipientIban: true,
    dueAt: true,
    comment: true,
  })
  .strict()
export type RequestSupplierOrderPaymentInput = z.infer<
  typeof requestSupplierOrderPaymentInputSchema
>
